import React from 'react';
import { Table, Pagination, Spinner, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ColumnSchema } from '../../types/customReport';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { formatDateInNurseryTimezone } from '../../utils/timezone';

interface Props {
  columns: ColumnSchema[];
  data: any[];
  total: number;
  page: number;
  perPage: number;
  loading: boolean;
  error: string | null;
  onPageChange: (page: number) => void;
  onSortChange: (column: string) => void;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  groupBy?: string;
  lastPage?: number;
  timezone?: string;
}

export const ReportPreviewTable: React.FC<Props> = ({
  columns,
  data,
  total,
  page,
  perPage,
  loading,
  error,
  onPageChange,
  onSortChange,
  sortBy,
  sortDir,
  groupBy,
  lastPage: lastPageProp,
  timezone = 'UTC',
}) => {
  const { t } = useTranslation(['pages', 'common']);
  const effectivePage = page || 1;
  const effectiveTotal = total || data.length;
  const lastPage = lastPageProp || Math.ceil(effectiveTotal / perPage);

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (loading && data.length === 0) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (data.length === 0) {
    return <div className="text-center py-5 text-muted">{t('customReports.noData')}</div>;
  }

  let lastGroupValue: any = null;

  const formatValue = (value: any, type: string) => {
    if (value === null || value === undefined) return '-';
    if (type === 'boolean') return value ? t('buttons.yes', { ns: 'common' }) : t('buttons.no', { ns: 'common' });
    if (type === 'date') {
      try {
        return formatDateInNurseryTimezone(value, timezone);
      } catch (e) {
        return value;
      }
    }
    return value.toString();
  };

  return (
    <div className="position-relative">
      {loading && (
        <div 
          className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(255,255,255,0.5)', zIndex: 10 }}
        >
          <Spinner animation="border" variant="primary" />
        </div>
      )}
      <div className="table-responsive">
        <Table hover striped bordered size="sm" style={{ fontSize: '0.85rem' }}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th 
                  key={col.key} 
                  className={col.sortable ? 'cursor-pointer' : ''}
                  onClick={() => col.sortable && onSortChange(col.key)}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    {col.label}
                    {sortBy === col.key && (
                      sortDir === 'asc' ? <ArrowUpward style={{ fontSize: '14px' }} /> : <ArrowDownward style={{ fontSize: '14px' }} />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.map((row, idx) => {
              const rows = [];
              if (groupBy) {
                const currentGroupValue = row[groupBy];
                if (currentGroupValue !== lastGroupValue) {
                  rows.push(
                    <tr key={`group-${currentGroupValue}-${idx}`} className="bg-light">
                      <td colSpan={columns.length} className="fw-bold">
                        {columns.find(c => c.key === groupBy)?.label}: {currentGroupValue}
                      </td>
                    </tr>
                  );
                  lastGroupValue = currentGroupValue;
                }
              }
              rows.push(
                <tr key={idx}>
                  {columns.map((col) => (
                    <td key={col.key}>{formatValue(row[col.key], col.type)}</td>
                  ))}
                </tr>
              );
              return rows;
            })}
          </tbody>
        </Table>
      </div>

      {lastPage > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="text-muted small">
            {t('pagination.showing', { 
              from: (effectivePage - 1) * perPage + 1, 
              to: Math.min(effectivePage * perPage, effectiveTotal), 
              total: effectiveTotal,
              ns: 'common' 
            })}
          </div>
          <Pagination size="sm" className="mb-0">
            <Pagination.First onClick={() => onPageChange(1)} disabled={effectivePage === 1} />
            <Pagination.Prev onClick={() => onPageChange(effectivePage - 1)} disabled={effectivePage === 1} />
            
            {[...Array(Math.min(5, lastPage))].map((_, i) => {
              let pageNum;
              if (lastPage <= 5) {
                pageNum = i + 1;
              } else if (effectivePage <= 3) {
                pageNum = i + 1;
              } else if (effectivePage >= lastPage - 2) {
                pageNum = lastPage - 4 + i;
              } else {
                pageNum = effectivePage - 2 + i;
              }
              return (
                <Pagination.Item
                  key={pageNum}
                  active={pageNum === effectivePage}
                  onClick={() => onPageChange(pageNum)}
                >
                  {pageNum}
                </Pagination.Item>
              );
            })}

            <Pagination.Next onClick={() => onPageChange(effectivePage + 1)} disabled={effectivePage === lastPage} />
            <Pagination.Last onClick={() => onPageChange(lastPage)} disabled={effectivePage === lastPage} />
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default ReportPreviewTable;
